import React, { useState, useEffect } from 'react';
import moment from 'moment';

import { SimpleDonutChart } from 'components/SimpleDonutChart';
import ContextualMenu from '../ContextualMenu';
import { ITEMS_TYPE, TAXONOMY_LABELS, SPEND_TYPE_LABELS } from '../utils';
import { Tooltip } from 'react-tippy';

import {
	Wrapper,
	Title,
	Table,
	TableHeaderCell,
	TableCell,
	InnerCellWrapper,
	SpendTableCell,
	HorizontalLine,
	TableRow,
	MenuWrapper,
	TooltipIcon,
	TooltipContent,
} from './InitiativesList.styles';
import { ApIcon, colors } from '@alixpartners/ui-components';
import orderBy from 'lodash/orderBy';
import { getFormattedCurrencyWithIdealDenomination } from 'utils/number';

const TABLE_HEADER_HEIGHT = 28;
const TABLE_ROW_HEIGHT = 40;
const TABLE_COLS = [
	{
		title: 'Initiative name',
		id: 'name',
	},
	{
		title: 'Type',
		id: 'type',
	},
	{
		title: 'Taxonomy',
		id: 'taxonomy',
	},
	{
		title: 'Spend',
		id: 'spend',
	},
	{
		title: 'Sourceable',
		id: 'addressable',
	},
	{
		title: 'Low',
		id: 'low',
	},
	{
		title: 'High',
		id: 'high',
	},
	{
		title: 'Creation date',
		id: 'date',
	},
];
const PAYMENT_TERMS_TABLE_COLS = [
	{
		title: 'Initiative name',
		id: 'name',
	},
	{
		title: 'Type',
		id: 'type',
	},
	{
		title: 'Taxonomy',
		id: 'taxonomy',
	},
	{
		title: 'Coverage',
		id: 'spend',
	},
	{
		title: 'WCI - Low',
		id: 'low',
	},
	{
		title: 'WCI - High',
		id: 'high',
	},
	{
		title: 'Creation date',
		id: 'date',
	},
];

export const InitiativesList = props => {
	const {
		data,
		currencySymbol,
		onEdit,
		onDelete,
		onDuplicate,
		isPaymentTerms,
		showTopVendors,
	} = props;
	const [isMenuOpen, setMenuOpen] = useState(false);
	const [hoveredRowIndex, setHoveredRowIndex] = useState();
	const [sorting, setSorting] = useState(
		// { column: 'date', ascending: true }
		{ column: 'high', ascending: false }
	);

	const sortData = (data, sorting) => {
		const { column, ascending } = sorting;
		const result = [...data];
		let sortCb = ascending
			? (a, b) => (a[column] > b[column] ? -1 : 1)
			: (a, b) => (a[column] > b[column] ? 1 : -1);
		if (sorting.column === 'date') {
			sortCb = ascending
				? (a, b) => (moment(a.createdDate) > moment(b.createdDate) ? -1 : 1)
				: (a, b) => (moment(a.createdDate) > moment(b.createdDate) ? 1 : -1);
		}
		if (sorting.column === 'high') {
			sortCb = ascending
				? (a, b) => (a.high > b.high ? 1 : -1)
				: (a, b) => (a.high > b.high ? -1 : 1);
		}
		if (sorting.column === 'taxonomy') {
			const order = ascending ? 'asc' : 'desc';

			return orderBy(
				data,
				['spendType', 'taxonomyType'],
				[order, order === 'asc' ? 'desc' : 'asc']
			);
		}

		result.sort(sortCb);

		return result;
	};

	const [sortedData, setSortedData] = useState(sortData(data, sorting));
	useEffect(
		() => {
			setSortedData(sortData(data, sorting));
		},
		[sorting, data]
	);

	const hideContextualTrigger = () => {
		if (!isMenuOpen) {
			setHoveredRowIndex(null);
		}
	};

	const getFormattedPrice = value => {
		let formattedPrice = getFormattedCurrencyWithIdealDenomination({
			value,
			isUnitSign: true,
			hideDecimals: false,
		});

		formattedPrice = formattedPrice
			? `${currencySymbol}${formattedPrice}`
			: formattedPrice;

		return formattedPrice;
	};

	const tableCols = isPaymentTerms ? PAYMENT_TERMS_TABLE_COLS : TABLE_COLS;

	return (
		<Wrapper onMouseLeave={hideContextualTrigger}>
			<Title>Initiatives ({data.length})</Title>
			<HorizontalLine />
			<Table colsCount={tableCols.length}>
				<TableRow>
					{tableCols.map(column => (
						<TableHeaderCell
							key={column.id}
							onClick={() =>
								setSorting({
									column: column.id,
									ascending:
										sorting.column === column.id ? !sorting.ascending : true,
								})
							}
						>
							<span>{column.title}</span>
							{column.id === sorting.column && (
								<ApIcon
									iconName={
										sorting.ascending ? 'arrow_upward' : 'arrow_downward'
									}
									iconColor={colors.grey4}
									iconSize={16}
								/>
							)}
						</TableHeaderCell>
					))}
				</TableRow>
				{sortedData.map((item, index) => (
					<TableRow
						key={item.id}
						onMouseMove={() => {
							if (hoveredRowIndex !== index) {
								setHoveredRowIndex(index);
								setMenuOpen(false);
							}
						}}
						isHovered={hoveredRowIndex === index}
					>
						<TableCell isRelative>
							{item.description && (
								<TooltipIcon isHidden={hoveredRowIndex !== index}>
									<Tooltip
										position="top"
										animation="fade"
										theme="light"
										arrow
										html={<TooltipContent>{item.description}</TooltipContent>}
									>
										<ApIcon
											iconColor={colors.grey5}
											iconSize={16}
											iconName="outline_info"
										/>
									</Tooltip>
								</TooltipIcon>
							)}
							<InnerCellWrapper>{item.name}</InnerCellWrapper>
						</TableCell>
						<TableCell>
							<InnerCellWrapper>{item.type}</InnerCellWrapper>
						</TableCell>
						<TableCell>
							<InnerCellWrapper>
								{SPEND_TYPE_LABELS[item.spendType]},{' '}
								{TAXONOMY_LABELS[item.taxonomyType]}
							</InnerCellWrapper>
						</TableCell>

						<SpendTableCell>
							<span>{item.spend}</span>
							<SimpleDonutChart
								data={item.chartData}
								currencySymbol={currencySymbol}
								paddingAngle={0}
							/>
						</SpendTableCell>
						{!isPaymentTerms && (
							<TableCell>
								{item.addressable} ({item.addressablePercentage}%)
							</TableCell>
						)}
						<TableCell>
							{item.low} ({item.lowPercentage}%)
						</TableCell>
						<TableCell>
							{getFormattedPrice(item.high)} ({item.highPercentage}%)
						</TableCell>
						<TableCell>
							{item.date}

							<MenuWrapper top={TABLE_HEADER_HEIGHT + index * TABLE_ROW_HEIGHT}>
								{hoveredRowIndex === index && (
									<ContextualMenu
										onEdit={() => onEdit(item.id)}
										onDelete={() => onDelete(item.id)}
										onDuplicate={() => onDuplicate(item.id)}
										showTopVendors={
											item.initiativeItemType === ITEMS_TYPE.CATEGORY ||
											item.initiativeItemType === ITEMS_TYPE.PT_IMPROVEMENT ||
											item.initiativeItemType === ITEMS_TYPE.TAIL_SPEND
												? () =>
														showTopVendors({
															initiativeId: item.id,
															name: item.name,
														})
												: null
										}
										isPaymentTerms={isPaymentTerms}
										toggleMenu={() => {
											setMenuOpen(!isMenuOpen);
										}}
										isOpen={hoveredRowIndex === index && isMenuOpen}
									/>
								)}
							</MenuWrapper>
						</TableCell>
					</TableRow>
				))}
			</Table>
		</Wrapper>
	);
};

export default InitiativesList;
